/**
 * A deployed site's identity → the card's `PackBusiness`.
 *
 * The backend (`GET /api/invite/:token/identity`) hands back the raw useful
 * fields of the live site's `business.json` / `theme.json`. Everything the card
 * actually needs on top of that — what colour the preset paints, which Google
 * Fonts the pair means, whether the logo's baked tint is stale — is resolved
 * here, in the browser, against the generated `presetPalettes` table. That is
 * the same reasoning `gen-packlab-data.mjs` does for /lab/pack, so a prospect's
 * card and the lab's card come out identical.
 */

import { cardBrand, logoTintOf, tradeLabel, type PackBusiness } from './BusinessCard';
import { FONT_PAIRS, PRESET_PALETTES } from './presetPalettes';

/**
 * Exactly what `GET /api/invite/:token/identity` returns.
 *
 * Two producers, and they must agree with this interface and each other:
 *   · apps/client-portal/backend/src/routes/invite.ts — `loadSiteIdentity()`,
 *     over the wire from the deployed site's `/_source/`;
 *   · apps/clonerator/lib/print-kit/identity.ts — the same payload read off
 *     the local `output/<slug>/` bundle, to drive the print-ready PDF kit
 *     (src/print/ renders these very components from it).
 */
export interface SiteIdentity {
  slug: string;
  domain: string;
  business: {
    name: string;
    phone: string | null;
    city: string | null;
    state: string | null;
    vertical: string | null;
    established: number | null;
    establishedIsEstimate: boolean;
    logo: string | null;
  };
  theme: {
    colors: string | null;
    fonts: string | null;
    logoFont: string | null;
    logoIsLogomark: boolean;
    /**
     * Whether the logo's own artwork/edges read as dark — `theme.logoDarkBackground`
     * off the site's bundle (fnd-lw0/fnd-p84). Optional: absent on bundles
     * generated before that flag existed, and on any producer of this
     * interface that hasn't been updated to forward it. `identityToPack`
     * passes it straight through to `PackBusiness.logoDarkBackground`, where
     * `LogoImage` uses `groundFromFlag` (see `./logoGround`) to skip its
     * canvas probe when the flag is present, and falls back to the probe when
     * it's undefined.
     */
    logoDarkBackground?: boolean;
    /**
     * Provenance of the logo enrichment ("website" | "facebook"). Not used
     * for ground detection — carried through because the wire payload already
     * has it and a future caller may want it.
     */
    logoSource?: string | null;
    /**
     * The logo's own measured background colour (`business.theme.logoBgHex`,
     * fnd-782n) — written by the generator only when the logo canvas is
     * solid, opaque and not white. When present, every pack face paints
     * itself in this colour (see `./logoSurface`), exactly as the site's
     * header does, so the logo blends in instead of sitting on the stock as
     * a stray tile. Absent on bundles that predate the measurement.
     */
    logoBgHex?: string | null;
    /** Whether the logo asset has real transparency (`business.theme.logoTransparent`). */
    logoTransparent?: boolean;
  };
  /**
   * Not on the wire (the invite endpoint never sends it) — the print kit
   * carries the enriched owner name in the same envelope so the card can
   * print a person under the mark. Only ever a name we actually enriched.
   */
  owner?: string;
}

/** theme.json's logoFont is usually a family name, occasionally a slug. */
const LOGO_FONT_SLUGS: Record<string, string> = {
  'plus-jakarta': 'Plus Jakarta Sans',
  'archivo-black': 'Archivo Black',
  'dm-serif': 'DM Serif Display',
  'instrument-serif': 'Instrument Serif',
  'space-grotesk': 'Space Grotesk',
};

export function normalizeLogoFont(value?: string | null): string | null {
  if (!value) return null;
  if (/[A-Z ]/.test(value)) return value; // already a family name
  return (
    LOGO_FONT_SLUGS[value] ??
    value
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

/**
 * Honest-business-age policy: a founding year is printed only when the
 * generator did not invent it. `establishedIsEstimate` marks the guesses, and
 * 2016 is the old fabricated default that predates that flag.
 */
export function honestEstablished(
  established: number | null,
  isEstimate: boolean
): number | null {
  if (!established) return null;
  if (isEstimate) return null;
  if (established === 2016) return null;
  return established;
}

/** Ink, when a site names a palette this build has never heard of. */
const INK = { primary: '#1e2228', accent: '#1e2228' };

export function identityToPack(
  identity: SiteIdentity,
  /** The owner, as a person — only ever a name we actually enriched. */
  owner = ''
): PackBusiness {
  const { business, theme } = identity;
  const preset = (theme.colors && PRESET_PALETTES[theme.colors]) || INK;
  const brand = cardBrand(preset);
  const logo = business.logo ?? null;
  const tint = logoTintOf(logo);
  const pair = (theme.fonts && FONT_PAIRS[theme.fonts]) || null;

  return {
    id: identity.slug,
    trade: tradeLabel(business.vertical || 'home services'),
    name: business.name,
    owner,
    phone: business.phone ?? '',
    city: [business.city, business.state].filter(Boolean).join(', '),
    domain: identity.domain,
    established: honestEstablished(business.established, business.establishedIsEstimate),
    logo,
    logoTintHex: tint,
    // Painted a colour this theme doesn't use — a retired palette, a re-rolled
    // theme, or the generator's old fallback blue. "Logo: auto" repaints it.
    logoTintStale: Boolean(tint && tint !== preset.primary),
    /**
     * Symbol-only logo — the card must render the name itself. `logoIsLogomark`
     * is set in almost no bundles, so the library stencils
     * (foundlio/logos/processed/logo-<vertical>-N), which are always wordless
     * marks, are recognised by path instead of trusting that flag.
     */
    logomark: theme.logoIsLogomark || /\/logos\/processed\/logo-/.test(logo ?? ''),
    logoDarkBackground: theme.logoDarkBackground,
    logoBgHex: theme.logoBgHex ?? null,
    fonts: { heading: pair?.heading ?? null, logo: normalizeLogoFont(theme.logoFont) },
    brand,
  };
}
